<script setup lang="ts">
import RangeSlider, { type RulerMark } from '@components/RangeSlider.vue';
import Typography from '@components/Typography.vue';
import { useLoading } from '@composables/useLoading';
import { formatNumber } from '@tools/number';
import { minBy } from 'lodash-es';
import InputNumber from 'primevue/inputnumber';
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';

interface RangeSliderInputProps {
  values: number[];
  minValue?: number | null;
  maxValue?: number | null;
  majorTicks?: number[];
  marks?: RulerMark[];
  minLabel?: string;
  maxLabel?: string;
  unit?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  inputContainerClass?: string;
}

const {
  values,
  minValue = null,
  maxValue = null,
  majorTicks = undefined,
  marks = undefined,
  minLabel = '',
  maxLabel = '',
  unit = '',
  minPlaceholder = '0',
  maxPlaceholder = undefined,
  inputContainerClass = 'lg:w-52',
} = defineProps<RangeSliderInputProps>();

const emit = defineEmits<{(e: 'update:minValue', value: number | null): void; (e: 'update:maxValue', value: number | null): void;}>();

const maxIndexLimit = computed<number>(() => Math.max(0, values.length - 1));

const rangeValues = ref<[ number, number ]>([ 0, maxIndexLimit.value ]);
const previousRangeValues = ref<[ number, number ]>([ 0, maxIndexLimit.value ]);

const {
  isLoading: isUpdatingFromInput,
  startLoading: startUpdatingFromInput,
  stopLoading: stopUpdatingFromInput,
} = useLoading();

const {
  isLoading: isUpdatingFromSlider,
  startLoading: startUpdatingFromSlider,
  stopLoading: stopUpdatingFromSlider,
} = useLoading();

const resolvedMaxPlaceholder = computed<string>(() => {
  if (maxPlaceholder !== undefined) {
    return maxPlaceholder;
  }

  const lastValue = values[maxIndexLimit.value];
  return lastValue !== undefined ? `${formatNumber(lastValue)}+` : '';
});

const isValid = computed<boolean>(() => {
  if (minValue === null || minValue === undefined) {
    return true;
  }

  if (maxValue === null || maxValue === undefined) {
    return true;
  }

  return minValue <= maxValue;
});

const getClosestIndex = (
  value: number | null | undefined,
  defaultIndex: number,
): number => {
  if (value == null) {
    return defaultIndex;
  }

  const closestValue = minBy(
    values,
    (sliderValue) => Math.abs(sliderValue - value),
  );

  return closestValue === undefined
    ? defaultIndex
    : values.indexOf(closestValue);
};

const syncSliderFromProps = async (): Promise<void> => {
  const minIndex = getClosestIndex(minValue, 0);
  const maxIndex = getClosestIndex(maxValue, maxIndexLimit.value);

  if (minIndex > maxIndex) {
    return;
  }

  startUpdatingFromInput();

  rangeValues.value = [ minIndex, maxIndex ];
  previousRangeValues.value = [ minIndex, maxIndex ];

  await nextTick();

  stopUpdatingFromInput();
};

watch(
  () => [ minValue, maxValue ],
  () => {
    if (isUpdatingFromSlider.value) {
      return;
    }

    syncSliderFromProps();
  },
  { immediate: true },
);

watch(
  rangeValues,
  (newValue) => {
    if (
      isUpdatingFromInput.value
      || !newValue
      || newValue.length < 2
    ) {
      return;
    }

    let [ minIndex, maxIndex ] = newValue;

    if (minIndex > maxIndex) {
      const [ prevMin ] = previousRangeValues.value;

      if (minIndex !== prevMin) {
        minIndex = maxIndex;
      } else {
        maxIndex = minIndex;
      }

      rangeValues.value = [ minIndex, maxIndex ];
      return;
    }

    previousRangeValues.value = [ minIndex, maxIndex ];

    const newMin = minIndex === 0 ? null : values[minIndex];
    const newMax = maxIndex === maxIndexLimit.value ? null : values[maxIndex];

    startUpdatingFromSlider();
    emit('update:minValue', newMin);
    emit('update:maxValue', newMax);

    nextTick(() => {
      stopUpdatingFromSlider();
    });
  },
  { deep: true },
);

const onMinInputChange = async (value: number | null): Promise<void> => {
  emit('update:minValue', value);

  const minIndex = getClosestIndex(value, 0);
  const maxIndex = getClosestIndex(maxValue, maxIndexLimit.value);

  if (minIndex > maxIndex) {
    return;
  }

  startUpdatingFromInput();

  rangeValues.value = [ minIndex, maxIndex ];
  previousRangeValues.value = [ minIndex, maxIndex ];

  await nextTick();

  stopUpdatingFromInput();
};

const onMaxInputChange = async (value: number | null): Promise<void> => {
  emit('update:maxValue', value);

  const minIndex = getClosestIndex(minValue, 0);
  const maxIndex = getClosestIndex(value, maxIndexLimit.value);

  if (minIndex > maxIndex) {
    return;
  }

  startUpdatingFromInput();

  rangeValues.value = [ minIndex, maxIndex ];
  previousRangeValues.value = [ minIndex, maxIndex ];

  await nextTick();

  stopUpdatingFromInput();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      :class="[
        'm-4 mb-0',
        'flex flex-col gap-2',
      ]"
    >
      <RangeSlider
        v-model="rangeValues"
        :values="values"
        :major-ticks="majorTicks"
        :marks="marks"
        :is-active="minValue !== null || maxValue !== null"
      />
    </div>

    <div class="flex flex-col gap-4 lg:flex-row">
      <div class="flex flex-1 flex-col">
        <Typography
          v-if="minLabel"
          variant="body-bold"
        >
          {{ minLabel }}
        </Typography>

        <div
          :class="[
            'relative flex items-center',
            inputContainerClass,
          ]"
        >
          <InputNumber
            :model-value="minValue"
            :placeholder="minPlaceholder"
            :min="0"
            class="w-full"
            input-class="w-full outline-none rounded-lg"
            @update:model-value="onMinInputChange"
          />

          <Typography
            v-if="unit"
            variant="body"
            color="gray-dark"
            class="pointer-events-none absolute right-3"
          >
            {{ unit }}
          </Typography>
        </div>
      </div>

      <div class="flex flex-1 flex-col">
        <Typography
          v-if="maxLabel"
          variant="body-bold"
        >
          {{ maxLabel }}
        </Typography>

        <div
          :class="[
            'relative flex items-center border',
            { '!border-red': !isValid },
            inputContainerClass,
          ]"
        >
          <InputNumber
            :model-value="maxValue"
            :placeholder="resolvedMaxPlaceholder"
            :min="0"
            class="w-full"
            :input-class="[
              'w-full outline-none rounded-lg',
              { '!border-red': !isValid },
            ]"
            @update:model-value="onMaxInputChange"
          />

          <Typography
            v-if="unit"
            variant="body"
            color="gray-dark"
            class="pointer-events-none absolute right-3"
          >
            {{ unit }}
          </Typography>
        </div>
      </div>
    </div>

    <slot />
  </div>
</template>
